import { useMemo } from "react";
//#region types
import { WintSalaryEnums } from "models/enums";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
//#endregion
import { CurrencyHelper, LocalizationHelperInstance } from "utils/helpers";
import * as Yup from "yup";
import SalaryDeviationHelper from "./salaryDeviationHelper";
import useDeviationPermissions from "components/salaryDeviation/usePermissions";
import { WintDeviationFormSelection } from "models/formTypes/salaryDeviation";

type CreateErrorFn = (params: { message: string }) => Yup.ValidationError;

const formatMaxTimeDisplay = (maxTotalMinutes: number): string => {
  const maxHours = Math.floor(maxTotalMinutes / 60);
  const maxMins = maxTotalMinutes % 60;
  return maxMins > 0 ? `${maxHours}h ${maxMins}min` : `${maxHours}h`;
};

const validateHours = (
  hoursValue: number,
  minutes: number,
  maxTotalMinutes: number,
  createError: CreateErrorFn
): true | Yup.ValidationError => {
  if (isNaN(hoursValue)) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Invalid),
    });
  }

  if (hoursValue < 0) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: "0" }),
    });
  }

  if (hoursValue > 24) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "24h 0min" }),
    });
  }

  if (hoursValue === 24 && minutes > 0) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "24h 0min" }),
    });
  }

  const totalMinutes = hoursValue * 60 + minutes;
  if (totalMinutes > maxTotalMinutes) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", {
        max: formatMaxTimeDisplay(maxTotalMinutes),
      }),
    });
  }

  return true;
};

const validateMinutes = (
  minutesValue: number,
  hours: number,
  maxTotalMinutes: number,
  createError: CreateErrorFn
): true | Yup.ValidationError => {
  if (isNaN(minutesValue)) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Invalid),
    });
  }

  if (minutesValue < 0) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: "0" }),
    });
  }

  if (hours === 24 && minutesValue > 0) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "24h 0min" }),
    });
  }

  if (minutesValue > 59) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "59" }),
    });
  }

  const totalMinutes = hours * 60 + minutesValue;
  if (totalMinutes > maxTotalMinutes) {
    return createError({
      message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", {
        max: formatMaxTimeDisplay(maxTotalMinutes),
      }),
    });
  }

  return true;
};

const isEmptyValue = (value: unknown): boolean => {
  return value === undefined || value === null || (value as unknown) === "";
};

/**
 * Hook that provides the Yup validation schema for the WintDeviationCreate form.
 * Validates based on both deviationType and deviation informationType.
 */
function useWintDeviationValidation(withdrawalCap?: number) {
  // The withdrawal schema has to gate on the same flag the section renders on. Without manage
  // rights the section is not rendered, so requiring its fields would make the form permanently
  // unsubmittable with no field to highlight — the generic "obligatoriska fält saknas" dead end.
  const { canManageSalary } = useDeviationPermissions();

  const validationSchema = useMemo(() => {
    const minZeroMessage = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", {
      min: "0",
    });

    return Yup.object<SalaryDeviationFormTypes.WintDeviationCreate>({
      employee: Yup.object<AutocompleteListItem>().required(TranslationKeys.Validations.Required),
      deviationType: Yup.mixed()
        .oneOf(Object.values(WintSalaryEnums.SalaryDeviationType))
        .required(TranslationKeys.Validations.Required),
      deviation: Yup.object<AutocompleteListItem>().required(TranslationKeys.Validations.Required),
      child: Yup.object<AutocompleteListItem>().when(
        FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ([deviation], schema) =>
          SalaryDeviationHelper.isParentalLeaveClassification(deviation)
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
      ),
      comment: Yup.string(),
      timeDeviation: Yup.object<SalaryDeviationFormTypes.TimeDeviationCreate>().when(
        [
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.DeviationType,
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ],
        ([deviationType, deviation], schema) =>
          SalaryDeviationHelper.shouldShowTimeDeviationSection(deviationType, deviation)
            ? schema.shape({
                dateRange: Yup.object()
                  .shape({
                    start: Yup.date()
                      .typeError(TranslationKeys.Validations.InvalidDate)
                      .required(TranslationKeys.Validations.Required),
                    end: Yup.date()
                      .typeError(TranslationKeys.Validations.InvalidDate)
                      .when(
                        "start",
                        ([start], schema: Yup.DateSchema) =>
                          (start && schema.min(start, TranslationKeys.Validations.ToDateLessThanFromDate)) || schema
                      ),
                  })
                  .required(TranslationKeys.Validations.Required),
                reportType: Yup.number().required(TranslationKeys.Validations.Required),
                fillDayHours: Yup.boolean(),
                time: Yup.object<SalaryDeviationFormTypes.HourMinutes>().when(
                  [
                    FormFieldConstants.SalaryDeviation.TimeDeviationCreate.ReportType,
                    FormFieldConstants.SalaryDeviation.TimeDeviationCreate.FillDayHours,
                    FormFieldConstants.SalaryDeviation.TimeDeviationCreate.DayHours,
                  ],
                  ([reportType, fillDayHours, dayHours], schema) =>
                    reportType === WintSalaryEnums.SalaryDeviationReportType.Time && !fillDayHours
                      ? schema.shape(
                          {
                            hours: Yup.number().test("hours-time-validation", function (value) {
                              const { parent, createError } = this;
                              const minutes = Number(parent.minutes) || 0;
                              const hoursValue = Number(value);

                              const template = (dayHours as SalaryDeviationFormTypes.HoursByDayWithMeta[]) ?? [];
                              const maxTotalMinutes =
                                template.length > 0 ? Math.max(...template.map((d) => Number(d.maxTotalMinutes))) : 480;

                              if (isEmptyValue(value) && !minutes) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              return validateHours(hoursValue, minutes, maxTotalMinutes, createError);
                            }),
                            minutes: Yup.number().test("minutes-time-validation", function (value) {
                              const { parent, createError } = this;
                              const hours = Number(parent.hours) || 0;
                              const minutesValue = Number(value);

                              const template = (dayHours as SalaryDeviationFormTypes.HoursByDayWithMeta[]) ?? [];
                              const maxTotalMinutes =
                                template.length > 0 ? Math.max(...template.map((d) => Number(d.maxTotalMinutes))) : 480;

                              if (isEmptyValue(value) && !hours) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              return validateMinutes(minutesValue, hours, maxTotalMinutes, createError);
                            }),
                          },
                          [
                            [
                              FormFieldConstants.SalaryDeviation.HourMinutes.Minutes,
                              FormFieldConstants.SalaryDeviation.HourMinutes.Hours,
                            ],
                          ]
                        )
                      : schema
                ),
                dayHours: Yup.array<SalaryDeviationFormTypes.HoursByDayWithMeta>().when(
                  [
                    FormFieldConstants.SalaryDeviation.TimeDeviationCreate.ReportType,
                    FormFieldConstants.SalaryDeviation.TimeDeviationCreate.FillDayHours,
                  ],
                  (
                    [reportType, fillDayHours]: [WintSalaryEnums.SalaryDeviationReportType, boolean],
                    schema: ArraySchema<SalaryDeviationFormTypes.HoursByDayWithMeta>
                  ) =>
                    reportType === WintSalaryEnums.SalaryDeviationReportType.Time && fillDayHours
                      ? schema.of(
                          Yup.object<SalaryDeviationFormTypes.HoursByDayWithMeta>().shape({
                            day: Yup.date()
                              .typeError(TranslationKeys.Validations.InvalidDate)
                              .required(TranslationKeys.Validations.Required),
                            hours: Yup.number().test("hours-validation", function (value) {
                              const { parent, createError } = this;
                              const maxTotalMinutes = Number(parent.maxTotalMinutes) || 480;
                              const minutes = Number(parent.minutes) || 0;
                              const hoursValue = Number(value);

                              if (isEmptyValue(value) && !minutes) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              return validateHours(hoursValue, minutes, maxTotalMinutes, createError);
                            }),
                            minutes: Yup.number().test("minutes-validation", function (value) {
                              const { parent, createError } = this;
                              const maxTotalMinutes = Number(parent.maxTotalMinutes) || 480;
                              const hours = Number(parent.hours) || 0;
                              const minutesValue = Number(value);

                              if (isEmptyValue(value) && !hours) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              return validateMinutes(minutesValue, hours, maxTotalMinutes, createError);
                            }),
                          })
                        )
                      : schema
                ),
                percentage: Yup.number(),
              })
            : schema
      ),

      // Amount - for DecimalValue informationType (WorkingHours/Compensation)
      amount: Yup.object<SalaryDeviationFormTypes.AmountCreate>().when(
        [
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.DeviationType,
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ],
        ([deviationType, deviation], schema) =>
          SalaryDeviationHelper.shouldShowAmountSection(deviationType, deviation)
            ? schema.shape({
                yearMonth: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required),
                sumDescriptions: Yup.array()
                  .of(
                    SalaryDeviationHelper.isHoursDeviation(deviation)
                      ? Yup.object<SalaryDeviationFormTypes.SumDescription>().shape(
                          {
                            id: Yup.number(),
                            hours: Yup.number().test("amount-hours-validation", function (value) {
                              const { parent, createError } = this;
                              const hoursValue = Number(value);

                              if (isEmptyValue(value) && isEmptyValue(parent.minutes)) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              if (isNaN(hoursValue)) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Invalid
                                  ),
                                });
                              }

                              if (hoursValue < 0) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Min,
                                    "",
                                    { min: "0" }
                                  ),
                                });
                              }

                              return true;
                            }),
                            minutes: Yup.number().test("amount-minutes-validation", function (value) {
                              const { parent, createError } = this;
                              const minutesValue = Number(value);

                              if (isEmptyValue(value) && isEmptyValue(parent.hours)) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Required
                                  ),
                                });
                              }

                              if (isEmptyValue(value)) return true;

                              if (isNaN(minutesValue)) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Invalid
                                  ),
                                });
                              }

                              if (minutesValue < 0) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Min,
                                    "",
                                    { min: "0" }
                                  ),
                                });
                              }

                              if (minutesValue > 59) {
                                return createError({
                                  message: LocalizationHelperInstance.getFormattedMessage(
                                    TranslationKeys.Validations.Max,
                                    "",
                                    { max: "59" }
                                  ),
                                });
                              }

                              return true;
                            }),
                            description: Yup.string(),
                          },
                          [
                            [
                              FormFieldConstants.SalaryDeviation.SumDescription.Minutes,
                              FormFieldConstants.SalaryDeviation.SumDescription.Hours,
                            ],
                          ]
                        )
                      : Yup.object<SalaryDeviationFormTypes.SumDescription>({
                          id: Yup.number(),
                          sum: SalaryDeviationHelper.isMileageDeviation(deviation)
                            ? Yup.number()
                                .required(TranslationKeys.Validations.Required)
                                .positive(TranslationKeys.Validations.MustBePositive)
                            : Yup.number().required(TranslationKeys.Validations.Required),
                          // Commission: the description becomes the payslip row label, so it may not be
                          // cleared. Negative sums stay valid — the engine writes credit-note rows.
                          description: SalaryDeviationHelper.isCommission(deviation)
                            ? Yup.string().trim().required(TranslationKeys.Validations.Required)
                            : Yup.string(),
                        })
                  )
                  .min(1, TranslationKeys.Validations.Required),
              })
            : schema
      ),
      travelCompensation: Yup.object<SalaryDeviationFormTypes.TravelCompensationCreate>().when(
        FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ([deviation], schema) =>
          SalaryDeviationHelper.isTravelCompensation(deviation)
            ? schema.shape({
                fromDate: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required),
                toDate: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required)
                  .when(
                    "fromDate",
                    ([fromDate], schema) =>
                      (fromDate && schema.min(fromDate, TranslationKeys.Validations.ToDateLessThanFromDate)) || schema
                  ),
                fromTime: Yup.string().nullable().required(TranslationKeys.Validations.Required),
                toTime: Yup.string().nullable().required(TranslationKeys.Validations.Required),
                mealDeductions: Yup.array()
                  .of(
                    Yup.object<SalaryDeviationFormTypes.SalaryDeviationMeal>()
                      .nullable()
                      .when("$", {
                        is: (value: any) => value !== null && value !== undefined,
                        then: (schema) =>
                          schema.shape({
                            id: Yup.number(),
                            day: Yup.date()
                              .typeError(TranslationKeys.Validations.InvalidDate)
                              .required(TranslationKeys.Validations.Required),
                            breakfast: Yup.boolean(),
                            lunch: Yup.boolean(),
                            dinner: Yup.boolean(),
                          }),
                        otherwise: (schema) => schema,
                      })
                  )
                  .nullable(),
                countryCode: Yup.mixed<AutocompleteListItem>()
                  .nullable()
                  .test(
                    "countryCode-required",
                    TranslationKeys.Validations.Required,
                    SalaryDeviationHelper.createTravelCompensationAutocompleteRequiredTest("countryCode-required")
                  ),
              })
            : schema
      ),

      // Time Balances (comp-time / Komp) - a single signed hours value.
      // Required and non-zero; negative is allowed (deducts from the comp bank).
      timeBalances: Yup.object<SalaryDeviationFormTypes.TimeBalancesCreate>().when(
        [
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.DeviationType,
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ],
        ([deviationType, deviation], schema) =>
          SalaryDeviationHelper.shouldShowTimeBalancesSection(deviationType, deviation)
            ? schema.shape({
                yearMonth: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required),
                value: Yup.number()
                  .typeError(TranslationKeys.Validations.Invalid)
                  .required(TranslationKeys.Validations.Required)
                  .test("comptime-non-zero", TranslationKeys.Validations.MustNotBeZero, (value) => value !== 0),
              })
            : schema
      ),

      // Withdrawals: one strictly positive amount, capped by the pot balance the server returned.
      // The cap is only advisory — it is undefined whenever it cannot be trusted (balances failed,
      // or edit mode, where the balance may already net out the deviation being edited). The
      // server is the authority on what a withdrawal may draw and refuses an over-draw on both the
      // create and update paths, so those windows are covered there rather than here.
      withdrawal: Yup.object<SalaryDeviationFormTypes.WithdrawalCreate>().when(
        [
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.DeviationType,
          FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ],
        ([deviationType, deviation], schema) =>
          canManageSalary && SalaryDeviationHelper.shouldShowWithdrawalSection(deviationType, deviation)
            ? schema.shape({
                yearMonth: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required),
                amount: Yup.number()
                  .typeError(TranslationKeys.Validations.Invalid)
                  .required(TranslationKeys.Validations.Required)
                  .positive(TranslationKeys.Validations.MustBePositive)
                  .test(
                    "withdrawal-within-cap",
                    LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.SalaryDeviation.WithdrawalExceedsBalance,
                      "",
                      {
                        max: withdrawalCap !== undefined ? CurrencyHelper.getFormattedCurrencyInSEK(withdrawalCap) : "",
                      }
                    ),
                    (value) => withdrawalCap === undefined || value === undefined || value <= withdrawalCap
                  ),
              })
            : schema
      ),

      // Fuel benefit: mirror backend rules so errors land on the field, not after the round trip.
      fuelBenefit: Yup.object<SalaryDeviationFormTypes.FuelBenefitCreate>().when(
        FormFieldConstants.SalaryDeviation.WintDeviationCreate.Deviation,
        ([deviation], schema) =>
          SalaryDeviationHelper.isFuelBenefit(deviation)
            ? schema.shape({
                yearMonth: Yup.date()
                  .typeError(TranslationKeys.Validations.InvalidDate)
                  .nullable()
                  .required(TranslationKeys.Validations.Required),
                fuelCost: Yup.number()
                  .typeError(TranslationKeys.Validations.Invalid)
                  .required(TranslationKeys.Validations.Required)
                  .moreThan(0, TranslationKeys.SalaryDeviation.FuelBenefitCostRequired),
                // Total check sits here: nested error on a visible field, not the generic toast.
                privateDistance: Yup.number()
                  .typeError(TranslationKeys.Validations.Invalid)
                  .required(TranslationKeys.Validations.Required)
                  .min(0, minZeroMessage)
                  .test(
                    "fuel-total-distance",
                    TranslationKeys.SalaryDeviation.FuelBenefitTotalDistanceRequired,
                    function (value) {
                      const business = SalaryDeviationHelper.toOptionalNumber(this.parent.businessDistance) ?? 0;
                      return (value ?? 0) + business > 0;
                    }
                  ),
                // Optional: cleared field is "", not a number.
                businessDistance: Yup.number()
                  .transform((value, original) => (original === "" ? undefined : value))
                  .typeError(TranslationKeys.Validations.Invalid)
                  .nullable()
                  .min(0, minZeroMessage),
              })
            : schema
      ),
    });
  }, [withdrawalCap, canManageSalary]);

  const deleteValidationSchema = useMemo(() => {
    return Yup.object<SalaryDeviationFormTypes.WintDeviationDelete>({
      periodSelection: Yup.string<WintDeviationFormSelection>()
        .oneOf(["openedPeriod", "selectedPeriod", "all"])
        .required(TranslationKeys.Validations.Required),
    });
  }, []);

  return { validationSchema, deleteValidationSchema };
}

export default useWintDeviationValidation;
